import { sha256 } from '@noble/hashes/sha256';
import { ripemd160 } from '@noble/hashes/ripemd160';
import { sha3_256 } from '@noble/hashes/sha3';
import { keccak_256 } from '@noble/hashes/sha3';
import { base58Encode } from './ed25519';
import { bytesToHex } from './wallet';

export function tronAddress(pubUncompressed: Uint8Array): string {
  const xy = pubUncompressed[0] === 0x04 ? pubUncompressed.slice(1) : pubUncompressed;
  const hash = keccak_256(xy);
  const addrBytes = hash.slice(-20);
  const payload = new Uint8Array(21);
  payload[0] = 0x41;
  payload.set(addrBytes, 1);
  return base58CheckEncode(payload);
}

export function cosmosAddress(pubUncompressed: Uint8Array): string {
  const compressed = compressPubKey(pubUncompressed);
  const hash160 = ripemd160(sha256(compressed));
  return bech32Encode('cosmos', 0, hash160);
}

export function aptosAddress(pub: Uint8Array): string {
  const buf = new Uint8Array(pub.length + 1);
  buf.set(pub, 0);
  buf[pub.length] = 0x00;
  const hash = sha3_256(buf);
  return '0x' + bytesToHex(hash);
}

export function stellarAddress(pub: Uint8Array): string {
  const versionByte = 6 << 3; // 48 = 'G' prefix
  const payload = new Uint8Array(1 + pub.length);
  payload[0] = versionByte;
  payload.set(pub, 1);
  const checksum = crc16XMODEM(payload);
  const full = new Uint8Array(payload.length + 2);
  full.set(payload, 0);
  full[payload.length] = checksum & 0xff;
  full[payload.length + 1] = (checksum >> 8) & 0xff;
  return base32Encode(full);
}

function compressPubKey(pub: Uint8Array): Uint8Array {
  if (pub.length !== 65 || pub[0] !== 0x04) return pub;
  const out = new Uint8Array(33);
  out[0] = (pub[64] & 1) === 0 ? 0x02 : 0x03;
  out.set(pub.slice(1, 33), 1);
  return out;
}

function base58CheckEncode(payload: Uint8Array): string {
  const first = sha256(payload);
  const second = sha256(first);
  const checksum = second.slice(0, 4);
  const full = new Uint8Array(payload.length + 4);
  full.set(payload, 0);
  full.set(checksum, payload.length);
  return base58Encode(full);
}

// Bech32 (BIP-173)
const BECH32_CHARSET = 'qpzry9x8gf2tvdw0s3jn54khce6mua7l';

function bech32Encode(hrp: string, witnessVersion: number, data: Uint8Array): string {
  const converted = convertBits(data, 8, 5, true);
  const values = new Uint8Array([witnessVersion, ...converted]);
  const checksum = bech32Checksum(hrp, values);
  const combined = new Uint8Array([...values, ...checksum]);
  let result = hrp + '1';
  for (const v of combined) result += BECH32_CHARSET[v];
  return result;
}

function bech32Checksum(hrp: string, data: Uint8Array): Uint8Array {
  const values = new Uint8Array([...hrpExpand(hrp), ...data, 0, 0, 0, 0, 0, 0]);
  const polymod = bech32Polymod(values) ^ 1;
  const out = new Uint8Array(6);
  for (let i = 0; i < 6; i++) out[i] = (polymod >> (5 * (5 - i))) & 31;
  return out;
}

function hrpExpand(hrp: string): Uint8Array {
  const out = new Uint8Array(hrp.length * 2 + 1);
  for (let i = 0; i < hrp.length; i++) out[i] = hrp.charCodeAt(i) >> 5;
  out[hrp.length] = 0;
  for (let i = 0; i < hrp.length; i++) out[hrp.length + 1 + i] = hrp.charCodeAt(i) & 31;
  return out;
}

function bech32Polymod(values: Uint8Array): number {
  const gen = [0x3b6a57b2, 0x26508e6d, 0x1ea119fa, 0x3d4233dd, 0x2a1462b3];
  let chk = 1;
  for (const v of values) {
    const b = chk >>> 25;
    chk = ((chk & 0x1ffffff) << 5) ^ v;
    for (let i = 0; i < 5; i++) {
      if ((b >> i) & 1) chk ^= gen[i];
    }
  }
  return chk;
}

function convertBits(data: Uint8Array, fromBits: number, toBits: number, pad: boolean): Uint8Array {
  let acc = 0;
  let bits = 0;
  const maxV = (1 << toBits) - 1;
  const result: number[] = [];
  for (const b of data) {
    acc = (acc << fromBits) | b;
    bits += fromBits;
    while (bits >= toBits) {
      bits -= toBits;
      result.push((acc >> bits) & maxV);
    }
  }
  if (pad && bits > 0) result.push((acc << (toBits - bits)) & maxV);
  return new Uint8Array(result);
}

function crc16XMODEM(data: Uint8Array): number {
  let crc = 0;
  for (const b of data) {
    crc ^= b << 8;
    for (let i = 0; i < 8; i++) {
      crc = (crc & 0x8000) ? ((crc << 1) ^ 0x1021) : (crc << 1);
    }
  }
  return crc & 0xffff;
}

const BASE32_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';

function base32Encode(data: Uint8Array): string {
  let bits = 0;
  let acc = 0;
  let result = '';
  for (const b of data) {
    acc = (acc << 8) | b;
    bits += 8;
    while (bits >= 5) {
      bits -= 5;
      result += BASE32_ALPHABET[(acc >> bits) & 0x1f];
    }
  }
  if (bits > 0) result += BASE32_ALPHABET[(acc << (5 - bits)) & 0x1f];
  while (result.length % 8 !== 0) result += '=';
  return result;
}
